import type { FinChronumSiteAccrualRow } from '@shared/api';
import { Table } from '@shared/ui/Table';
import { formatMoney } from '../financierFormat';
import {
  TableWrap,
  RightTh,
  RightTd,
  TableNoticeTd,
  BoldTitle,
  SiteSubText,
  GreenProjectText,
  MutedText,
  ShiftDiffText,
  GrossAccruedText,
  CanteenSiteTd,
  TotalLaborCost,
} from './styled';
import { monthLabel } from './model';

interface PayrollSitesTableProps {
  sites: FinChronumSiteAccrualRow[];
  /** Месяц сводки (первое число). */
  periodMonth: string;
  /** Текущий месяц: только у него «на смене» — это сейчас. */
  isCurrentMonth: boolean;
}

export function PayrollSitesTable({ sites, periodMonth, isCurrentMonth }: PayrollSitesTableProps) {
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <th>Объект строительства</th>
            <th>Штатная численность</th>
            <th>Смены Хронум vs Рапорты</th>
            <RightTh>Ставка смены</RightTh>
            <RightTh>Начисленный ФОТ</RightTh>
            <RightTh>НДФЛ и Взносы</RightTh>
            <RightTh>Удержания столовой</RightTh>
            <RightTh>Итого затраты на труд</RightTh>
          </tr>
        </thead>
        <tbody>
          {sites.length === 0 ? (
            <tr>
              <TableNoticeTd colSpan={8}>
                Данных по объектам за {monthLabel(periodMonth)} нет
              </TableNoticeTd>
            </tr>
          ) : (
            sites.map((s: FinChronumSiteAccrualRow) => {
              const diff = s.shifts_worked_mtd - s.field_reported_shifts_mtd;
              return (
                <tr key={s.id}>
                  <td>
                    <BoldTitle>{s.project_name}</BoldTitle>
                    <SiteSubText>
                      Площадка: {s.chronum_site_name} · {monthLabel(s.period_month)}
                    </SiteSubText>
                  </td>
                  <td>
                    <div>Прикреплено: <b>{s.workers_attached}</b> чел.</div>
                    {isCurrentMonth && (
                      <GreenProjectText>
                        На смене сейчас: {s.on_shift_now}
                      </GreenProjectText>
                    )}
                  </td>
                  <td>
                    <div>Хронум: <b>{s.shifts_worked_mtd}</b> смен</div>
                    <SiteSubText>
                      Рапорты прорабов: {s.field_reported_shifts_mtd}
                    </SiteSubText>
                    <ShiftDiffText $isOk={Math.abs(diff) <= 5}>
                      Разница: {diff > 0 ? `+${diff}` : diff} смен
                    </ShiftDiffText>
                  </td>
                  <RightTd>
                    <b>{formatMoney(s.avg_shift_rate_rub)}</b> / смена
                  </RightTd>
                  <RightTd>
                    <GrossAccruedText>{formatMoney(s.accrued_gross_fot_rub)}</GrossAccruedText>
                  </RightTd>
                  <RightTd>
                    <div>НДФЛ: {formatMoney(s.accrued_ndfl_rub)}</div>
                    <MutedText>
                      Взносы: {formatMoney(s.accrued_insurance_rub)}
                    </MutedText>
                  </RightTd>
                  <CanteenSiteTd>
                    −{formatMoney(s.canteen_deduction_rub)}
                  </CanteenSiteTd>
                  <RightTd>
                    <TotalLaborCost>
                      {formatMoney(s.total_labor_cost_rub)}
                    </TotalLaborCost>
                  </RightTd>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </TableWrap>
  );
}
